import { createContext, useContext, useState, useCallback, useEffect } from "react"
import { lumi } from "@/lib/lumi"
import toast from "react-hot-toast"

interface PendingEdit {
  collection: string
  id: string
  field: string
  value: string
}

interface EditModeContextType {
  isEditMode: boolean
  toggleEditMode: () => void
  pendingEdits: PendingEdit[]
  addEdit: (edit: PendingEdit) => void
  saveAllEdits: () => Promise<void>
  discardEdits: () => void
  isSaving: boolean
  contentEdits: Record<string, string>
  addContentEdit: (key: string, value: string) => void
}

const STORAGE_KEY = "ljj_site_content_edits"

const EditModeContext = createContext<EditModeContextType>({
  isEditMode: false,
  toggleEditMode: () => {},
  pendingEdits: [],
  addEdit: () => {},
  saveAllEdits: async () => {},
  discardEdits: () => {},
  isSaving: false,
  contentEdits: {},
  addContentEdit: () => {},
})

export function EditModeProvider({ children }: { children: React.ReactNode }) {
  const [isEditMode, setIsEditMode] = useState(false)
  const [pendingEdits, setPendingEdits] = useState<PendingEdit[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [contentEdits, setContentEdits] = useState<Record<string, string>>({})
  const [unsavedContentEdits, setUnsavedContentEdits] = useState<Record<string, string>>({})

  // Charger les contenus sauvegardés depuis localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        setContentEdits(JSON.parse(saved))
      }
    } catch (err) {
      console.error("Erreur chargement contenu:", err)
    }
  }, [])

  const toggleEditMode = useCallback(() => {
    setIsEditMode((prev) => {
      if (prev) {
        // Quand on désactive sans sauvegarder, on annule les changements non sauvegardés
        setPendingEdits([])
        setUnsavedContentEdits({})
      }
      return !prev
    })
  }, [])

  const addEdit = useCallback((edit: PendingEdit) => {
    setPendingEdits((prev) => {
      const existing = prev.findIndex(
        (e) => e.collection === edit.collection && e.id === edit.id && e.field === edit.field
      )
      if (existing >= 0) {
        const updated = [...prev]
        updated[existing] = edit
        return updated
      }
      return [...prev, edit]
    })
  }, [])

  const addContentEdit = useCallback((key: string, value: string) => {
    setUnsavedContentEdits((prev) => ({ ...prev, [key]: value }))
  }, [])

  const saveAllEdits = useCallback(async () => {
    const hasPendingEdits = pendingEdits.length > 0
    const hasContentEdits = Object.keys(unsavedContentEdits).length > 0

    if (!hasPendingEdits && !hasContentEdits) return

    setIsSaving(true)
    try {
      // Sauvegarder les éditions de collection (site_pages)
      if (hasPendingEdits) {
        const grouped: Record<string, any> = {}
        for (const edit of pendingEdits) {
          const key = `${edit.collection}:${edit.id}`
          if (!grouped[key]) {
            grouped[key] = { meta: { collection: edit.collection, id: edit.id }, fields: {} }
          }
          grouped[key].fields[edit.field] = edit.value
        }

        const promises = Object.values(grouped).map(async (group: any) => {
          const updateData = { ...group.fields, updatedAt: new Date().toISOString() }
          const entity = (lumi.entities as any)[group.meta.collection]
          if (entity) {
            await entity.update(group.meta.id, updateData)
          }
        })

        await Promise.all(promises)
      }

      // Sauvegarder les contenus inline dans localStorage
      if (hasContentEdits) {
        const merged = { ...contentEdits, ...unsavedContentEdits }
        setContentEdits(merged)
        localStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
        setUnsavedContentEdits({})
      }

      setPendingEdits([])
      toast.success("Modifications sauvegardées avec succès !")
    } catch (err) {
      console.error("Erreur de sauvegarde:", err)
      toast.error("Erreur lors de la sauvegarde")
    } finally {
      setIsSaving(false)
    }
  }, [pendingEdits, unsavedContentEdits, contentEdits])

  const discardEdits = useCallback(() => {
    setPendingEdits([])
    setUnsavedContentEdits({})
    setIsEditMode(false)
  }, [])

  // Combiner les éditions sauvegardées et les non-sauvegardées pour l'affichage
  const allContentEdits = { ...contentEdits, ...unsavedContentEdits }
  const totalPending = pendingEdits.length + Object.keys(unsavedContentEdits).length

  return (
    <EditModeContext.Provider
      value={{
        isEditMode,
        toggleEditMode,
        pendingEdits,
        addEdit,
        saveAllEdits,
        discardEdits,
        isSaving,
        contentEdits: allContentEdits,
        addContentEdit,
      }}
    >
      {children}
    </EditModeContext.Provider>
  )
}

export function useEditMode() {
  return useContext(EditModeContext)
}
